import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './PlayerList.css';

const PlayerList = () => {
  const { user, isAdmin, isAuthenticated } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasApprovedPlayer, setHasApprovedPlayer] = useState(false);
  const [checkingPlayer, setCheckingPlayer] = useState(true);
  const [userPlayer, setUserPlayer] = useState(null);
  const [userClubs, setUserClubs] = useState([]);
  const [playerClubsMap, setPlayerClubsMap] = useState(new Map()); // Map of playerId -> array of club names
  const [userClubIds, setUserClubIds] = useState(new Set()); // Set of club IDs the user is a member of
  
  // Check if user can view skill levels (admin or creator)
  const canViewSkillLevel = () => {
    if (isAdmin()) return true;
    if (user?.roles && user.roles.includes('TEAM_CREATOR')) return true;
    return false;
  };

  // Check if user can view email addresses (admin or club admin)
  const canViewEmail = () => {
    if (isAdmin()) return true;
    if (user?.roles && user.roles.includes('CLUB_ADMIN')) return true;
    return false;
  };

  /** Matches backend: full directory vs club-scoped */
  const hasElevatedDirectoryAccess = () => {
    if (isAdmin()) return true;
    const roles = user?.roles || [];
    return (
      roles.includes('CLUB_ADMIN') ||
      roles.includes('TEAM_CREATOR') ||
      roles.includes('CLUB_CREATOR')
    );
  };

  const checkApprovedPlayer = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setCheckingPlayer(false);
      return;
    }

    try {
      const allPlayers = await api.getAllPlayers(user?.id);
      const foundPlayer = allPlayers.find(p => p.userId === user.id);
      setHasApprovedPlayer(!!foundPlayer);
      setUserPlayer(foundPlayer || null);
      
      // Load user's clubs to check if they are part of any clubs
      if (foundPlayer) {
        try {
          const clubs = await api.getMyClubs(user.id);
          setUserClubs(clubs || []);
          // Store club IDs for filtering
          const clubIds = new Set(clubs.map(c => c.id));
          setUserClubIds(clubIds);
        } catch (err) {
          setUserClubs([]);
          setUserClubIds(new Set());
        }
      } else {
        setUserClubs([]);
        setUserClubIds(new Set());
      }
    } catch (err) {
      setHasApprovedPlayer(false);
      setUserPlayer(null);
      setUserClubs([]);
    } finally {
      setCheckingPlayer(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    checkApprovedPlayer();
  }, [checkApprovedPlayer]);

  const loadPlayerClubsMapping = useCallback(async (playersList) => {
    try {
      // Get all approved clubs
      const allClubs = await api.getApprovedClubs();
      const clubsMap = new Map(); // clubId -> club name
      const playerClubsById = new Map(); // playerId -> Set of club IDs
      allClubs.forEach(club => {
        clubsMap.set(club.id, club.name);
      });

      // For each club, get approved members and build player -> clubs mapping
      const playerClubs = new Map(); // playerId -> array of club names
      
      const membershipChecks = allClubs.map(async (club) => {
        try {
          const members = await api.getClubMembers(club.id, 'APPROVED', user?.id);
          members.forEach(member => {
            if (member.playerId) {
              // Track club IDs for each player
              if (!playerClubsById.has(member.playerId)) {
                playerClubsById.set(member.playerId, new Set());
              }
              playerClubsById.get(member.playerId).add(club.id);
              
              // Track club names for display
              if (!playerClubs.has(member.playerId)) {
                playerClubs.set(member.playerId, []);
              }
              const clubName = clubsMap.get(club.id);
              if (clubName) {
                playerClubs.get(member.playerId).push(clubName);
              }
            }
          });
        } catch (err) {
          console.warn(`Failed to load members for club ${club.id}:`, err);
        }
      });

      await Promise.all(membershipChecks);
      setPlayerClubsMap(playerClubs);
      
      // Store player clubs by ID for filtering
      return playerClubsById;
    } catch (err) {
      console.error('Failed to load player clubs mapping:', err);
      setPlayerClubsMap(new Map());
      return new Map();
    }
  }, [user?.id]);

  const loadPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAllPlayers(user?.id);

      // Backend restricts PLAYER-only users to shared clubs; map club labels for the rows returned
      await loadPlayerClubsMapping(data);

      setPlayers(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [loadPlayerClubsMapping, user?.id]);

  useEffect(() => {
    // Load players if checking is complete and user can view players
    // (admin, club creator, or has approved player record)
    if (!checkingPlayer) {
      const canView = hasApprovedPlayer || isAdmin() || (user?.roles && user.roles.includes('CLUB_CREATOR'));
      if (canView) {
        loadPlayers();
      } else {
        // User cannot view players, stop loading
        setLoading(false);
      }
    }
  }, [checkingPlayer, hasApprovedPlayer, userClubIds, user, isAdmin, loadPlayers]);

  const getSkillBadgeClass = (skill) => {
    if (skill >= 8) return 'high';
    if (skill >= 5) return 'medium';
    return 'low';
  };

  const formatPosition = (position) => {
    return position.charAt(0) + position.slice(1).toLowerCase().replace('_', ' ');
  };

  if (checkingPlayer || loading) {
    return <div className="loading">Loading...</div>;
  }

  // Check if user is approved player, admin, or club creator
  const canViewPlayers = hasApprovedPlayer || isAdmin() || (user?.roles && user.roles.includes('CLUB_CREATOR'));

  if (!canViewPlayers) {
    return (
      <div className="card">
        <h2>Access Restricted</h2>
        <div className="alert alert-warning">
          <p>You need to be an approved player to view other players.</p>
          <p>Please wait for system admin approval after registering as a player.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div>
      <div className="card">
        <h2>
          {isAdmin()
            ? 'All Players'
            : hasElevatedDirectoryAccess()
              ? 'Players'
              : 'Players from My Clubs'}{' '}
          ({players.length})
        </h2>
        {!hasElevatedDirectoryAccess() && userClubIds.size > 0 && (
          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Showing only players from clubs you are an approved member of
          </p>
        )}
        <button onClick={loadPlayers} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
          Refresh
        </button>
      </div>

      {/* Show message if current user is not part of any clubs */}
      {hasApprovedPlayer && userPlayer && userClubs.length === 0 && !hasElevatedDirectoryAccess() && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <strong>You are not a member of any clubs yet</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            You can only view players from clubs you are a member of. Request to join a club or wait for a club admin to add you to their club.
          </p>
        </div>
      )}

      {players.length === 0 ? (
        <div className="empty-state">
          <h3>No players registered yet</h3>
          <p>Be the first to register!</p>
        </div>
      ) : (
        <div className="player-list">
          {players.map((player) => {
            const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api\/?$/, '') || 'http://localhost:8080';
            const profileImgUrl = player.profilePictureUrl
              ? (player.profilePictureUrl.startsWith('http') ? player.profilePictureUrl : `${apiBase}${player.profilePictureUrl}`)
              : null;
            return (
            <div key={player.id} className="player-card">
              <div className="player-card-header">
                {profileImgUrl ? (
                  <img src={profileImgUrl} alt={player.name} className="player-card-avatar" />
                ) : (
                  <div className="player-card-avatar-placeholder">{player.name.charAt(0).toUpperCase()}</div>
                )}
                <h3>{player.name}</h3>
              </div>
              {canViewEmail() && (
                <div className="player-info">
                  <strong>Email:</strong> {player.email}
                </div>
              )}
              {player.phoneNumber && (
                <div className="player-info">
                  <strong>Phone:</strong> {player.phoneNumber}
                </div>
              )}
              <div className="player-info">
                <strong>Position:</strong> {formatPosition(player.position)}
              </div>
              {canViewSkillLevel() && (
                <div className="player-info">
                  <strong>Skill Level:</strong>{' '}
                  <span className={`skill-badge ${getSkillBadgeClass(player.skillLevel)}`}>
                    {player.skillLevel}/10
                  </span>
                </div>
              )}
              {playerClubsMap.has(player.id) && playerClubsMap.get(player.id).length > 0 && (
                <div className="player-info">
                  <strong>Clubs:</strong>{' '}
                  <span style={{ color: 'var(--color-brand)', fontWeight: '500' }}>
                    {playerClubsMap.get(player.id).join(', ')}
                  </span>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerList;

